import React, { useState, useEffect, useCallback } from 'react';

import { Link } from 'react-router-dom';

import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Alert from 'react-bootstrap/Alert';
import Col from 'react-bootstrap/Col';

import * as api from 'api/pricing';

const PricingSummary = React.memo(({ match }) => {
  const {
    params: { strategy },
    url
  } = match;

  const [error, setError] = useState(null);
  const [versions, setVersions] = useState([]);
  const [originalPricingVersion, setOriginalPricingVersion] = useState('');
  const [pricingVersion, setPricingVersion] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);

    async function getPricingVersions() {
      try {
        const pricingVersions = await api.getPricingVersions(strategy);
        const { id: current } = await api.getCurrentPricingVersion(strategy);

        setOriginalPricingVersion(current);
        setPricingVersion(current);
        setVersions(pricingVersions);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    }

    getPricingVersions();
  }, [strategy]);

  const handleSelectChange = useCallback(event => {
    setPricingVersion(parseInt(event.target.value, 10));
  }, []);

  const handleCancelClick = useCallback(() => {
    setPricingVersion(originalPricingVersion);
  }, [originalPricingVersion]);

  const handleSaveClick = useCallback(async () => {
    setSaving(true);
    try {
      await api.putPricingVersion(pricingVersion);
      setOriginalPricingVersion(parseInt(pricingVersion, 10));
      setSaving(false);
    } catch (error) {
      setError(error);
    }
  }, [pricingVersion]);

  const allowEdition = !!pricingVersion && originalPricingVersion !== pricingVersion;

  const { tag: currentPricingVersion } = versions.find(v => v.id === originalPricingVersion) || {};

  return (
    <div>
      {!!error && <Alert variant="danger">We got unexpected error</Alert>}
      {!!loading && (
        <div className="mt-4 d-flex justify-content-center">
          <Spinner animation="border" />
        </div>
      )}
      {!loading && (
        <>
          <Form>
            <p>
              Current pricing version: <strong>{currentPricingVersion}</strong>
            </p>
            <Form.Group as={Row}>
              <Form.Label column sm={3}>
                Select the new pricing version
              </Form.Label>
              <Col sm={3}>
                <Form.Control as="select" value={pricingVersion} onChange={handleSelectChange}>
                  {versions.map(version => (
                    <option key={version.id} value={version.id}>
                      {version.tag}
                    </option>
                  ))}
                </Form.Control>
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="d-flex justify-content-end">
              {!!allowEdition && (
                <Col sm={6}>
                  <Button
                    variant="light"
                    disabled={!allowEdition}
                    className="mr-2"
                    onClick={handleCancelClick}
                  >
                    Cancel
                  </Button>
                  <Button disabled={!allowEdition || saving} onClick={handleSaveClick}>
                    {!!saving && <Spinner animation="border" variant="light" size="sm" />}
                    {!saving && <>Apply new pricing version</>}
                  </Button>
                </Col>
              )}
            </Form.Group>
            <div className="mt-5">
              <Link to={`${url}/create`}>
                <Button variant="outline-primary">Create new one</Button>
              </Link>
            </div>
          </Form>
        </>
      )}
    </div>
  );
});

export default PricingSummary;
